import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { Box, Spinner } from '@chakra-ui/react';

import { useApi } from '@app/hooks';
import { usePermissions } from '@app/hooks/use-permissions';
import { ROLES } from '@app/navigation/types';

import { SkuCatalog } from './catalog';
import { SkuLinearTable } from './table';
import { SkuTree } from './tree';
import { SkuLookups, SkuViewMode } from './types';

export const SkuManagement: FC = () => {
  const api = useApi();
  const canChange = usePermissions({ roles: [ROLES.ADMIN, ROLES.CHIEF] });

  const [mode, setMode] = useState<SkuViewMode>('linear');
  const [loading, setLoading] = useState(true);
  const [lookups, setLookups] = useState<Omit<SkuLookups, 'categoriesById' | 'subcategoriesById' | 'unitsById'>>({
    categories: [],
    subcategories: [],
    units: [],
  });

  useEffect(() => {
    (async () => {
      setLoading(true);
      const [categoriesRes, subcategoriesRes, unitsRes] = await Promise.all([
        api.sku.getCategories(),
        api.sku.getSubcategories(),
        api.sku.getUnits(),
      ]);
      setLookups({
        categories: categoriesRes?.data || [],
        subcategories: subcategoriesRes?.data || [],
        units: unitsRes?.data || [],
      });
      setLoading(false);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fullLookups: SkuLookups = useMemo(
    () => ({
      ...lookups,
      categoriesById: lookups.categories.reduce<Record<number, string>>((acc, item) => {
        acc[item.id] = item.name;
        return acc;
      }, {}),
      subcategoriesById: lookups.subcategories.reduce<Record<number, string>>((acc, item) => {
        acc[item.id] = item.name;
        return acc;
      }, {}),
      unitsById: lookups.units.reduce<Record<number, string>>((acc, item) => {
        acc[item.id] = item.name;
        return acc;
      }, {}),
    }),
    [lookups]
  );

  const onModeChange = useCallback((next: SkuViewMode) => setMode(next), []);

  if (loading) {
    return (
      <Box width="100%" height="300px" display="flex" justifyContent="center" alignItems="center">
        <Spinner />
      </Box>
    );
  }

  if (mode === 'catalog') {
    return (
      <SkuCatalog
        lookups={fullLookups}
        canChange={canChange}
        mode={mode}
        onModeChange={onModeChange}
      />
    );
  }

  if (mode === 'tree') {
    return (
      <SkuTree
        lookups={fullLookups}
        canChange={canChange}
        mode={mode}
        onModeChange={onModeChange}
      />
    );
  }

  return (
    <SkuLinearTable
      lookups={fullLookups}
      canChange={canChange}
      mode={mode}
      onModeChange={onModeChange}
    />
  );
};

export default SkuManagement;
